import { Stack, useTheme } from '@mui/material';
import { keys } from '#helpers/otherHelpers.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { areDiscoverMovieFilters, areMovieFormikValues } from '../helpers/areDiscoverFilters.ts';
import { filterOutEmptyValues } from '../helpers/filterOutEmptyValues.ts';
import { areOptions } from '../helpers/otherHelpers.ts';
import { transformMovieFiltersToValues, transformTvFiltersToValues } from '../helpers/transformFiltersToValues.ts';
import { transformValueIntoChips } from '../helpers/transformValuesIntoChips.ts';
import { transformMovieValuestoFilters, transformTvValuestoFilters } from '../helpers/transformValuestoFilters.ts';
import { type ComponentProps, type MovieFormikValues, type TvFormikValues } from '../types.ts';
import { SelectedChip } from './SelectedChip.tsx';

export const SelectedChipStack = ({ filterOptions, filtersParam, setFiltersParam, type }: ComponentProps) => {
  const { config } = useToolbelt();
  const theme = useTheme();

  const formValues = areDiscoverMovieFilters(filtersParam, type)
    ? transformMovieFiltersToValues(filtersParam, filterOptions)
    : transformTvFiltersToValues(filtersParam, filterOptions);

  const filteredFormValues = filterOutEmptyValues(formValues);

  if (Object.keys(filteredFormValues).length === 0) {
    return;
  }

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        backgroundColor: theme.backgroundSecondary,
        borderBottom: `1px solid ${theme.palette.grey[500]}`,
        boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2)',
        flexWrap: 'wrap',
        marginX: `-${theme.spacing(1)}`,
        paddingBottom: theme.spacing(2),
        paddingTop: theme.spacing(3),
        paddingX: theme.spacing(1),
        position: 'sticky',
        [theme.breakpoints.down('sm')]: {
          paddingTop: '20px',
          top: '105px',
        },
        top: '112px',
        zIndex: 3,
      }}
      useFlexGap
    >
      {keys(filteredFormValues).map(key => {
        const filteredFormValue = filteredFormValues[key];
        const chips = transformValueIntoChips(key, filteredFormValue, filterOptions, type);

        return chips.map(chip => (
          <SelectedChip
            {...chip}
            key={chip.id}
            onDelete={() => {
              const newFormValues: MovieFormikValues | TvFormikValues = { ...formValues };
              const value = newFormValues[key];

              if (areOptions(value)) {
                const entries = [...value];
                // @ts-expect-error Based on above code, we can assign entries
                // back to newFormValues[key]
                newFormValues[key] = entries.filter(entry => entry.id !== chip.id);

                if (newFormValues[key].length === 0) {
                  // Am okay with this
                  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                  delete newFormValues[key];
                }
              } else {
                // Am okay with this
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                delete newFormValues[key];
              }

              const newFiltersParam = areMovieFormikValues(newFormValues, type)
                ? transformMovieValuestoFilters(newFormValues, filterOptions, config)
                : transformTvValuestoFilters(newFormValues);

              setFiltersParam(newFiltersParam);
            }}
            type={key}
          />
        ));
      })}
    </Stack>
  );
};
